import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  QueryClient,
  useQuery,
  skipToken,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { ApiError } from '@/shared/http';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLingui } from '@lingui/react/macro';
import { Check } from 'lucide-react';
import { useEffect, useId, useRef, useState, type SyntheticEvent, type ReactNode } from 'react';
import {
  FormProvider,
  useForm,
  useWatch,
  type DefaultValues,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form';
import type { z } from 'zod';

import { useIsMobile, useOverlayParam } from '@/shared/mobile';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/ui/drawer';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet';

import { FormStateProvider } from './fields';

export interface EntitySheetProps<V extends FieldValues> {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly schema: z.ZodType<V, V>;
  /** Valores ao abrir: o item em edição ou o vazio do "adicionar". */
  readonly defaultValues: V;
  /** "Adicionar envolvido" / "Salvar envolvido": o verbo muda com o modo (o legado dizia "Adicionar" nos dois). */
  readonly submitLabel: string;
  /**
   * Recebe os valores já validados. Devolva `false` para ficar aberto (depois de um `form.setError`,
   * por exemplo); qualquer outra coisa fecha. Pode ser assíncrono.
   */
  readonly onSubmit: (values: V, form: UseFormReturn<V>) => unknown;
  readonly children: ReactNode | ((form: UseFormReturn<V>) => ReactNode);
  /** iPhone: tela cheia com barra "Cancelar · título · Salvar" (padrão) ou bottom sheet (formulário curto). */
  readonly mobile?: 'screen' | 'sheet';
  readonly readOnly?: boolean;
  /** Salvar só quando `true` com os valores atuais (um upload em andamento, por exemplo). */
  readonly canSubmit?: (values: V) => boolean;
  /** Rótulo do botão que fecha sem salvar ("Fechar" onde o legado dizia assim); padrão "Cancelar". */
  readonly cancelLabel?: string;
  /** Rótulo curto do salvar na barra do iPhone (tela cheia); padrão "Salvar". */
  readonly mobileSubmitLabel?: string;
}

interface BodyProps<V extends FieldValues> extends EntitySheetProps<V> {
  readonly layout: 'web' | 'screen' | 'sheet';
}

function EntitySheetBody<V extends FieldValues>({
  title,
  description,
  schema,
  defaultValues,
  submitLabel,
  onSubmit,
  onOpenChange,
  children,
  readOnly = false,
  canSubmit,
  cancelLabel,
  mobileSubmitLabel,
  layout,
}: BodyProps<V>) {
  const { t } = useLingui();
  const closeLabel = readOnly ? t`Fechar` : (cancelLabel ?? t`Cancelar`);
  const formId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const [busy, setBusy] = useState(false);
  const [errorEpoch, setErrorEpoch] = useState(0);
  const form = useForm<V, unknown, V>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues as DefaultValues<V>,
    mode: 'onTouched',
    shouldFocusError: false,
  });
  const watched = useWatch({ control: form.control });
  const submitDisabled = canSubmit ? !canSubmit(watched as V) : false;

  const focused = useRef(0);
  useEffect(() => {
    if (errorEpoch === 0 || focused.current === errorEpoch) return;
    const first = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!first) return;
    first.focus();
    focused.current = errorEpoch;
  });

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    // O sheet mora num portal, mas o evento do React sobe pela árvore até o <form> do formulário pai.
    event.stopPropagation();
    if (readOnly) return;
    void form.handleSubmit(
      async (values) => {
        setBusy(true);
        try {
          const result = await onSubmit(values, form);
          if (result === false) setErrorEpoch((epoch) => epoch + 1);
          else onOpenChange(false);
        } finally {
          setBusy(false);
        }
      },
      () => setErrorEpoch((epoch) => epoch + 1),
    )();
  };

  const fields = (
    <FormProvider {...form}>
      <FormStateProvider readOnly={readOnly}>
        <form
          id={formId}
          ref={formRef}
          noValidate
          onSubmit={submit}
          className="flex flex-col gap-4"
        >
          {typeof children === 'function' ? children(form) : children}
        </form>
      </FormStateProvider>
    </FormProvider>
  );
  const save = readOnly ? null : (
    <Button
      type="submit"
      form={formId}
      loading={busy}
      disabled={submitDisabled}
      size={layout === 'web' ? 'default' : 'lg'}
      className={layout === 'sheet' ? 'w-full' : undefined}
    >
      <Check aria-hidden />
      {submitLabel}
    </Button>
  );

  if (layout === 'screen') {
    return (
      <>
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-2 pt-[env(safe-area-inset-top,0px)]">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {closeLabel}
          </Button>
          <SheetTitle className="min-w-0 flex-1 truncate text-center text-base">{title}</SheetTitle>
          {readOnly ? (
            <span className="w-20" />
          ) : (
            <Button
              type="submit"
              form={formId}
              variant="link"
              loading={busy}
              disabled={submitDisabled}
              className="px-3!"
            >
              {mobileSubmitLabel ?? t`Salvar`}
            </Button>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+24px)]">
          {description ? (
            <SheetDescription className="mb-4">{description}</SheetDescription>
          ) : (
            <SheetDescription className="sr-only">{title}</SheetDescription>
          )}
          {fields}
        </div>
      </>
    );
  }

  if (layout === 'sheet') {
    return (
      <>
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
          {description ? (
            <DrawerDescription>{description}</DrawerDescription>
          ) : (
            <DrawerDescription className="sr-only">{title}</DrawerDescription>
          )}
        </DrawerHeader>
        <DrawerBody>{fields}</DrawerBody>
        {save ? <DrawerFooter className="border-t border-border pt-3">{save}</DrawerFooter> : null}
      </>
    );
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>{title}</SheetTitle>
        {description ? (
          <SheetDescription>{description}</SheetDescription>
        ) : (
          <SheetDescription className="sr-only">{title}</SheetDescription>
        )}
      </SheetHeader>
      <SheetBody>{fields}</SheetBody>
      <SheetFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          {closeLabel}
        </Button>
        {save}
      </SheetFooter>
    </>
  );
}

/**
 * Formulário de um item em painel (P11): lateral na web, tela cheia ou bottom sheet no iPhone. Tem o
 * próprio `useForm` (zod), isolado do formulário pai, e o submit não vaza para o pai.
 */
export function EntitySheet<V extends FieldValues>(props: EntitySheetProps<V>) {
  const isMobile = useIsMobile();
  const layout = isMobile ? (props.mobile ?? 'screen') : 'web';
  if (layout === 'sheet') {
    return (
      <Drawer open={props.open} onOpenChange={props.onOpenChange} repositionInputs={false}>
        <DrawerContent data-slot="entity-sheet">
          <EntitySheetBody {...props} layout="sheet" />
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange}>
      <SheetContent
        data-slot="entity-sheet"
        size="sm"
        showClose={layout === 'web'}
        className={cn(layout === 'screen' && 'max-w-none border-l-0')}
      >
        <EntitySheetBody {...props} layout={layout} />
      </SheetContent>
    </Sheet>
  );
}

export interface SheetState<C> {
  readonly open: boolean;
  readonly id: string | undefined;
  readonly isLoading: boolean;
  readonly error: ApiError | null;
  /** O item em edição; `undefined` no "adicionar" ou depois de recarregar a página com o sheet na URL. */
  readonly context: C | undefined;
  readonly show: (context?: C) => void;
  readonly setOpen: (open: boolean) => void;
}

/**
 * Sheet aberto na URL (`?sheet=<nome>`, U26): o voltar do navegador e o gesto do iPhone fecham o sheet
 * em vez de sair da tela. O contexto (item em edição) fica em memória.
 */
export interface SheetEntityOptions<C, K extends QueryKey = QueryKey> {
  readonly idKey?: string;
  readonly getId: (entity: C) => string;
  readonly entityQuery: (id: string) => UseQueryOptions<C, ApiError, C, K>;
}

const detachedSheetClient = new QueryClient();

export function useSheetState<C, K extends QueryKey = QueryKey>(
  name: string,
  options?: SheetEntityOptions<C, K>,
): SheetState<C> {
  const [open, setOpenParam] = useOverlayParam(name);
  const search: Record<string, unknown> = useSearch({ strict: false });
  const navigate = useNavigate();
  const idKey = options?.idKey ?? 'edit';
  const rawId = search[idKey];
  const id =
    options && open && typeof rawId === 'string' && rawId !== '' && rawId !== 'novo'
      ? rawId
      : undefined;
  const [memory, setMemory] = useState<C | undefined>(undefined);
  const saved =
    options && open && memory !== undefined && options.getId(memory) !== id ? undefined : memory;
  const queryOptions = options && id ? options.entityQuery(id) : undefined;
  const query = useQuery<C, ApiError, C, K>(
    {
      ...queryOptions,
      queryKey: queryOptions?.queryKey ?? (['sheet', name, id ?? ''] as unknown as K),
      queryFn: queryOptions?.queryFn ?? skipToken,
      enabled: !!queryOptions && saved === undefined && open,
      placeholderData: (previous) => (open ? undefined : previous),
    },
    options ? undefined : detachedSheetClient,
  );
  const setOpen = (next: boolean) => {
    if (!options) {
      setOpenParam(next);
      return;
    }
    void navigate({
      to: '.',
      replace: !next,
      search: (previous: Record<string, unknown>) => {
        const { [idKey]: _id, sheet: _sheet, ...rest } = previous;
        return next ? { ...previous, sheet: name } : rest;
      },
    });
  };
  return {
    open,
    id,
    isLoading: !!queryOptions && saved === undefined && query.isPending,
    error: query.error,
    context: options ? (saved ?? query.data) : memory,
    show: (next) => {
      setMemory(() => next);
      if (!options) {
        setOpenParam(true);
        return;
      }
      void navigate({
        to: '.',
        search: (previous: Record<string, unknown>) => ({
          ...previous,
          sheet: name,
          [idKey]: next === undefined ? undefined : options.getId(next),
        }),
      });
    },
    setOpen,
  };
}
